import { useNavigate } from "react-router-dom";
import { Home, LogOut, Settings } from "lucide-react";
import ThemeToggle from "@/components/ThemeToggle";
import NavigationMenu from "@/components/NavigationMenu";
import mascot from "@/assets/bailbuddies-icon.png";

interface BailBuddiesHeaderProps {
  showAuth?: boolean;
  showSettings?: boolean;
  showHome?: boolean;
  showNavMenu?: boolean;
  onSignOut?: () => void;
}

const IconButton = ({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    className="sticker-press flex h-11 w-11 items-center justify-center rounded-[14px] border-3 border-sticker-ink bg-sticker-surface text-sticker-ink shadow-sticker-3"
  >
    {children}
  </button>
);

const BailBuddiesHeader = ({
  showAuth = false,
  showSettings = false,
  showHome = false,
  showNavMenu = true,
  onSignOut,
}: BailBuddiesHeaderProps) => {
  const navigate = useNavigate();
  const shouldShowNavMenu = showNavMenu && !showAuth;

  return (
    <header className="sticky top-0 z-50 border-b-4 border-sticker-ink bg-sticker-page/95 font-sticker-body text-sticker-ink backdrop-blur-xl">
      <div className="mx-auto flex max-w-[1280px] items-center justify-between gap-3 px-4 py-3 sm:px-6 md:px-10">
        <div className="flex min-w-0 items-center gap-2.5">
          {shouldShowNavMenu && (
            <div className="rounded-[14px] border-3 border-sticker-ink bg-sticker-surface shadow-sticker-3">
              <NavigationMenu />
            </div>
          )}

          {showHome && (
            <IconButton label="Go to dashboard" onClick={() => navigate("/dashboard")}>
              <Home className="h-5 w-5" />
            </IconButton>
          )}

          <button
            type="button"
            onClick={() => navigate(showAuth ? "/" : "/dashboard")}
            className="sticker-press flex min-w-0 items-center gap-2.5 rounded-[18px] border-3 border-sticker-ink bg-sticker-surface py-1.5 pl-1.5 pr-3 shadow-sticker-3"
            aria-label={showAuth ? "Go to BailBuddies home" : "Go to dashboard"}
          >
            <img
              src={mascot}
              alt="BailBuddies ghost mascot"
              className="h-9 w-9 rounded-[12px] border-2 border-sticker-ink object-cover"
            />
            <span className="hidden truncate font-sticker-display text-[21px] tracking-[-0.03em] sm:inline">
              <span className="text-sticker-purple">Bail</span>
              <span className="text-sticker-ink">Buddies</span>
            </span>
          </button>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate("/pricing")}
            className="sticker-press hidden rounded-full border-3 border-sticker-ink bg-sticker-yellow px-4 py-2 text-[13px] font-extrabold shadow-sticker-3 sm:inline-flex"
          >
            Pricing
          </button>

          <div className="rounded-[14px] border-3 border-sticker-ink bg-sticker-surface shadow-sticker-3">
            <ThemeToggle />
          </div>

          {showSettings && (
            <IconButton label="Open settings" onClick={() => navigate("/settings")}>
              <Settings className="h-5 w-5" />
            </IconButton>
          )}

          {onSignOut && (
            <IconButton label="Sign out" onClick={onSignOut}>
              <LogOut className="h-5 w-5" />
            </IconButton>
          )}

          {showAuth && (
            <>
              <button
                type="button"
                onClick={() => navigate("/auth")}
                className="sticker-press hidden rounded-full border-3 border-sticker-ink bg-sticker-surface px-4 py-2 text-[13px] font-extrabold shadow-sticker-3 sm:inline-flex"
              >
                Sign in
              </button>
              <button
                type="button"
                onClick={() => navigate("/auth?next=/create")}
                className="sticker-press rounded-full border-3 border-sticker-ink bg-sticker-purple px-4 py-2 text-[13px] font-extrabold text-sticker-on-purple shadow-sticker-3"
              >
                Start a plan
              </button>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default BailBuddiesHeader;
